/* UI strings in the three launch languages (ADR-19). `{x}` placeholders; plural forms via Intl.PluralRules.
   Each language is its own chunk (app/locales/): the active one loads before the first render and on a switch, so a
   page ships one language, not three (roadmap v2 P16/D7). */
import { createContext, useContext } from "react";
import type en from "./locales/en";

export type Lang = "tr" | "de" | "en";
export type Dict = typeof en;
export type Key = keyof Dict;

const LOADERS: Record<Lang, () => Promise<{ default: Dict }>> = {
  en: () => import("./locales/en"),
  tr: () => import("./locales/tr"),
  de: () => import("./locales/de"),
};
const loaded: Partial<Record<Lang, Dict>> = {};

/** Loads a language's strings once; the app renders a language only after this resolved. */
export async function loadLang(lang: Lang): Promise<void> {
  loaded[lang] ??= (await LOADERS[lang]()).default;
}

export function translate(lang: Lang, key: Key, vars?: Record<string, string | number>): string {
  let s = loaded[lang]?.[key] ?? loaded.en?.[key] ?? key;
  if (vars) for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(String(v));
  return s;
}

export function detectLang(): Lang {
  try {
    const saved = localStorage.getItem("lumi_ui");
    if (saved === "tr" || saved === "de" || saved === "en") return saved;
  } catch { /* storage blocked */ }
  const n = (navigator.language || "en").slice(0, 2).toLowerCase();
  return n === "tr" || n === "de" ? n : "en";
}

export function langName(code: string, ui: Lang): string {
  try { return new Intl.DisplayNames([ui], { type: "language" }).of(code) ?? code; } catch { return code; }
}

export const I18nContext = createContext<{ lang: Lang; setLang: (l: Lang) => void }>({ lang: "en", setLang: () => undefined });

export function useT() {
  const { lang } = useContext(I18nContext);
  return (key: Key, vars?: Record<string, string | number>) => translate(lang, key, vars);
}
export function useLang() { return useContext(I18nContext); }

/** Every language has the same keys, so any loaded one answers. */
export function isKey(k: string): k is Key { return Object.values(loaded).some(d => k in d); }
